import React from 'react';
import { OwnershipType } from '@/lib/types';
import { Button, Col, DatePicker, Form, Radio, Row, Space, Typography } from 'antd';
import type { FormInstance } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

interface OwnershipOption {
  label: string;
  value: OwnershipType;
}

interface OwnershipStatusFieldsProps {
  form: FormInstance;
  typeFieldName: string;
  startDateFieldName: string;
  endDateFieldName: string;
  question: string;
  helpText: string;
  options: OwnershipOption[];
  required?: boolean;
  requiredMessage?: string;
  disabled?: boolean;
  allowClear?: boolean;
  clearLabel?: string;
}

const getDateValueProps = (value?: string) => ({
  value: value ? dayjs(value) : null,
});

const normalizeDate = (value?: Dayjs | null) => (value ? value.format('YYYY-MM-DD') : undefined);

export const OwnershipStatusFields: React.FunctionComponent<OwnershipStatusFieldsProps> = ({
  form,
  typeFieldName,
  startDateFieldName,
  endDateFieldName,
  question,
  helpText,
  options,
  required = false,
  requiredMessage = 'Selecteer een optie',
  disabled = false,
  allowClear = false,
  clearLabel = 'Wis antwoord',
}) => {
  const selectedValue = Form.useWatch(typeFieldName, form) as OwnershipType | undefined;

  const clearSelection = () => {
    form.setFieldsValue({
      [typeFieldName]: undefined,
      [startDateFieldName]: undefined,
      [endDateFieldName]: undefined,
    });
  };

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Form.Item
        label={question}
        name={typeFieldName}
        rules={required ? [{ required: true, message: requiredMessage }] : undefined}
        style={{ marginBottom: selectedValue !== undefined ? 0 : 24 }}
      >
        <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: '8px' }} disabled={disabled}>
          {options.map(option => (
            <Radio key={option.value} value={option.value}>
              {option.label}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>
      {allowClear && selectedValue !== undefined && !disabled && (
        <Button onClick={clearSelection} style={{ paddingLeft: 0, width: 'fit-content' }} type="link">
          {clearLabel}
        </Button>
      )}
      {selectedValue !== undefined && (
        <>
          <Typography.Text type="secondary">{helpText}</Typography.Text>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item getValueProps={getDateValueProps} label="Begindatum" name={startDateFieldName} normalize={normalizeDate}>
                <DatePicker allowClear disabled={disabled} format="DD-MM-YYYY" placeholder="Selecteer datum" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item getValueProps={getDateValueProps} label="Einddatum" name={endDateFieldName} normalize={normalizeDate}>
                <DatePicker allowClear disabled={disabled} format="DD-MM-YYYY" placeholder="Selecteer datum" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
    </Space>
  );
};
